import { inject, Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { ApiService } from './api.service';
import { BASE_URL } from './injection-tokens';

export interface EligibilityLookupRequest {
  sourceId: string;
  memberId?: string;
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
}

export interface EligibilityAccumulatorDto {
  type: string;
  coverageLevel: string;
  network: string;
  limit?: number | null;
  remaining?: number | null;
  spent?: number | null;
  met: boolean;
}

export interface ServiceBenefitDesignDto {
  category: string;
  serviceCode: string;
  serviceName: string;
  network: string;
  coverageLevel?: string | null;
  deductibleRule?: string | null;
  serviceDeductibleAmount?: number | null;
  copayAmount?: number | null;
  coinsurancePercent?: number | null;
  priorAuthRequired?: boolean | null;
  messages: string[];
  summary: string;
}

export interface HdhpAssessmentDto {
  status: 'None' | 'Named' | 'LikelyQualified' | string;
  label?: string | null;
  reason?: string | null;
  thresholdYear?: number | null;
}

export interface EligibilityLookupResponse {
  sourceId: string;
  memberId?: string | null;
  subscriberName?: string | null;
  planName?: string | null;
  planStatus?: string | null;
  accumulators: EligibilityAccumulatorDto[];
  serviceDesigns: ServiceBenefitDesignDto[];
  hdhpAssessment?: HdhpAssessmentDto | null;
  raw?: unknown;
}

export abstract class EligibilityServiceBase {
  abstract getSources(): Promise<string[]>;
  abstract lookup(request: EligibilityLookupRequest, includeRaw?: boolean): Promise<EligibilityLookupResponse>;
  abstract lookupByMember(
    sourceId: string,
    memberId: string,
    includeRaw?: boolean
  ): Promise<EligibilityLookupResponse>;
}

@Injectable({ providedIn: 'root' })
export class EligibilityService extends EligibilityServiceBase {
  protected _api: ApiService = inject(ApiService);
  protected _baseUrl: string = inject(BASE_URL);

  override getSources(): Promise<string[]> {
    return lastValueFrom(
      this._api.makeAPIGetCall<string[]>(this._baseUrl, '/eligibility/sources')
    );
  }

  override lookup(
    request: EligibilityLookupRequest,
    includeRaw = true
  ): Promise<EligibilityLookupResponse> {
    if (request.memberId && !request.firstName && !request.lastName && !request.dateOfBirth) {
      return this.lookupByMember(request.sourceId, request.memberId, includeRaw);
    }

    return lastValueFrom(
      this._api.makeAPIPostCall<EligibilityLookupResponse>(
        this._baseUrl,
        '/eligibility/lookup',
        request,
        includeRaw ? { includeRaw: 'true' } : undefined
      )
    );
  }

  override lookupByMember(
    sourceId: string,
    memberId: string,
    includeRaw = true
  ): Promise<EligibilityLookupResponse> {
    const encodedSourceId = encodeURIComponent(sourceId);
    const encodedMemberId = encodeURIComponent(memberId);
    return lastValueFrom(
      this._api.makeAPIGetCall<EligibilityLookupResponse>(
        this._baseUrl,
        `/eligibility/${encodedSourceId}/${encodedMemberId}`,
        includeRaw ? { includeRaw: 'true' } : undefined
      )
    );
  }
}
